import { useEffect, useState } from "react";
import { MessageSquare, Loader2, RefreshCw, Trash2, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { adminFeedback, adminDeleteFeedback, apiError } from "@/lib/adminApi";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function FeedbackPanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => adminFeedback().then(setData).catch((e) => toast.error(apiError(e, "Could not load feedback")));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback entry?")) return;
    try { await adminDeleteFeedback(id); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e, "Delete failed")); }
  };

  if (!data) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const rows = data.rows.filter((r) => (filter === "all" ? true : filter === "comments" ? !!r.comment : r.rating === filter));
  const pct = data.total ? Math.round((data.up / data.total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="admin-feedback">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0F172A] text-white"><MessageSquare className="h-5 w-5" /></span>
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Result feedback</h2>
            <p className="text-sm text-slate-500" data-testid="admin-feedback-summary">
              {data.total} responses · <span className="font-semibold text-emerald-700">{data.up} up</span> · <span className="font-semibold text-rose-700">{data.down} down</span> · {pct}% positive · {data.with_comment} with comments
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-feedback-filter" className="min-h-[40px] rounded-md border border-slate-300 bg-white px-3 text-sm">
            <option value="all">All</option><option value="up">Thumbs up</option><option value="down">Thumbs down</option><option value="comments">With comment</option>
          </select>
          <button onClick={load} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /> Refresh</button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">No feedback yet.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.feedback_id} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-feedback-row-${i}`}>
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${r.rating === "up" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {r.rating === "up" ? <ThumbsUp className="h-4 w-4" /> : <ThumbsDown className="h-4 w-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800">{r.comment || <span className="italic text-slate-400">No comment</span>}</p>
                <p className="mt-1 text-xs text-slate-500">{fmt(r.created_at)} · {r.page === "stream_result" ? `Stream result${r.stream_id ? ` · ${r.stream_id}` : ""}` : "Full results"}{r.assessment_id ? ` · assessment ${r.assessment_id.slice(0, 8)}…` : ""}</p>
              </div>
              <button onClick={() => remove(r.feedback_id)} data-testid={`admin-feedback-delete-${i}`} aria-label="Delete" className="rounded-md p-2 text-slate-400 hover:bg-[#FEF2F2] hover:text-[#C8102E]"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
